import PropTypes from 'prop-types';
import { useCallback, useMemo, useState } from 'react';
import { isEqual, isNull, mapValues, pick, round } from 'lodash-es';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown, faArrows } from '@fortawesome/free-solid-svg-icons';

import { useObservable, useObservableState } from 'observable-hooks';
import { useQuery } from '@apollo/client';
import { distinctUntilChanged, map, skipWhile, switchMap } from 'rxjs';
import { convertUnits, distanceConverter } from '../../../utils/formatting';

import { AlarmLinesGraph, createEmptyAlarmPoints } from './AlarmLinesGraph';
import { useWatchObservable } from '../../reactForm/utils';
import { LayoutHelper } from '../../reactForm/layout-helper';
import { RoundedButton } from '../../form/Button';

import './alarm-preview-graph.scss';
import { alarmLineQuery } from '../../../graph/queries';
import LoadingSpinner from '../../loadingSpinner/LoadingSpinner';
import { SwarmType } from '../../../enums';
import { getFormSchema } from './schema-utils';
import { switchHead } from '../../../utils/rxjs';

function initializeAlarm(alarmLevelIndex) {
    return {
        points: createEmptyAlarmPoints(),
        alarmLevelIndex,
    };
}

const EMPTY_CATEGORY_ALARM = initializeAlarm();

function useMeasurementSettings() {
    const values$ = useWatchObservable();

    const measurementSettingsFields = useMemo(() => {
        const formSchema = getFormSchema(SwarmType.VIBRATION, false);
        const layoutHelper = new LayoutHelper(formSchema.layout, formSchema.fields);
        return layoutHelper.getFieldsStartingAt('guideLine');
    }, []);

    const measurementSettings$ = useObservable(
        (inputs$) =>
            inputs$.pipe(
                switchMap(([incomingValues$, incomingMeasurementSettings]) =>
                    incomingValues$.pipe(
                        map((values) =>
                            mapValues(
                                pick(values, Object.keys(incomingMeasurementSettings)),
                                // Convert the values to the units defined in the form schema.
                                (value, key) =>
                                    convertUnits(incomingMeasurementSettings[key], value, true)
                            )
                        ),
                        distinctUntilChanged((previous, current) => isEqual(previous, current))
                    )
                )
            ),
        [values$, measurementSettingsFields]
    );

    return useObservableState(measurementSettings$, null);
}

function useCategoryWatcher() {
    const measurementSettings = useMeasurementSettings();

    const { data, error, loading } = useQuery(alarmLineQuery, {
        variables: {
            config: JSON.stringify({}),
            override: JSON.stringify({
                ...measurementSettings,
                // Ensure `selfDefinedAlarmLine` is always passed as an integer,
                // since it may come as a string from the `ChoiceField` component.
                selfDefinedAlarmLine: parseInt(measurementSettings?.selfDefinedAlarmLine ?? 0, 10),
            }),
        },
        skip: !measurementSettings,
    });

    const alarm = useMemo(() => {
        // `line` can be null in case of SBR-B, DIN4150-2 and
        // configs with an unavailable self defined line.
        if (error || !data?.alarmLine?.line) {
            return EMPTY_CATEGORY_ALARM;
        }

        return {
            alarmLevelIndex: null,
            points: data.alarmLine.line.map(({ vkar, freq }) => ({
                freq,
                vkar: vkar && round(distanceConverter(vkar), 2),
            })),
        };
    }, [data, error]);

    return [alarm, loading];
}

function useAlarmLevelsWatcher() {
    const notificationConfig$ = useWatchObservable(`notificationConfig`);

    const alarmLevels$ = useObservable(
        (inputs$) =>
            inputs$.pipe(
                switchHead(),
                map((config) => config.maxAlarms.map((alarm) => alarm.alarmLevel)),
                // Skip the first emission if it's an empty array.
                // Since the initial state of alarm levels is already set as an empty
                // array, re-emitting an empty array on the first emission is redundant
                // and unnecessarily triggers updates.
                skipWhile((alarmLevels, index) => index === 0 && alarmLevels.length === 0)
            ),
        [notificationConfig$]
    );

    return useObservableState(alarmLevels$, []);
}

export function AlarmPreviewGraph({ toggleVisibility }) {
    const [isExpanded, setIsExpanded] = useState(false);
    const [categoryAlarm, loading] = useCategoryWatcher();

    const alarmLevels = useAlarmLevelsWatcher();

    const alarms = useMemo(
        () => [
            categoryAlarm,
            ...alarmLevels.map((alarmLevel, index) => {
                const percentageLevel = 100 - alarmLevel;

                return {
                    alarmLevelIndex: index,
                    points: categoryAlarm.points.map(({ freq, vkar }) => ({
                        freq,
                        vkar: isNull(vkar) ? null : vkar - (percentageLevel / 100) * vkar,
                    })),
                };
            }),
        ],
        [categoryAlarm, alarmLevels]
    );

    return (
        <div
            className={`alarm_preview_graph z-50 ${
                isExpanded ? 'origin-bottom-right scale-150' : ''
            }`}
        >
            <div className="flex justify-between border border-b-0 border-graph-border-grey bg-white p-5 pb-0">
                {/* Empty div below acts a spacer to get the title centered. */}
                <div className="flex-1">{loading && <LoadingSpinner />}</div>
                <span className="text-lg">{gettext('ALARM_LEVELS')}</span>
                <div className="flex flex-1 justify-end gap-x-2">
                    <button
                        title={gettext('EXPAND_COLLAPSE_GRAPH')}
                        onClick={() => {
                            setIsExpanded((current) => !current);
                        }}
                    >
                        <FontAwesomeIcon icon={faArrows} />
                    </button>
                    <button title={gettext('MINIMIZE_GRAPH')} onClick={toggleVisibility}>
                        <FontAwesomeIcon icon={faAngleDown} />
                    </button>
                </div>
            </div>
            <AlarmLinesGraph alarms={alarms} />
        </div>
    );
}
AlarmPreviewGraph.propTypes = {
    toggleVisibility: PropTypes.func.isRequired,
};

export function AlarmPreviewGraphContainer() {
    const [isVisible, setIsVisible] = useState(true);

    const toggleVisibility = useCallback(() => {
        setIsVisible((current) => !current);
    }, []);

    return (
        <div className="fixed bottom-24 right-5">
            {isVisible ? (
                <AlarmPreviewGraph toggleVisibility={toggleVisibility} />
            ) : (
                <RoundedButton variant="big" onClick={toggleVisibility}>
                    {gettext('PREVIEW_OF_ALARM_LIMIT')}
                </RoundedButton>
            )}
        </div>
    );
}
AlarmPreviewGraphContainer.propTypes = {};
